import "./PromotionStep2.scss";

import * as formik from "formik";
import * as yup from "yup";

import { Col, Form, Modal, Row } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";

import AppButton from "../../../../commons/buttons/AppButton";
import AppCurrencyInput from "../../../../commons/inputs/AppCurrencyInput";
import AppInput from "../../../../commons/inputs/AppInput";
import AppLinkButton from "../../../../commons/buttons/AppLinkButton";
import CategoryAPI from "../../../services/CategoryAPI";
import CompanyAPI from "../../../company/CompanyAPI";
import HallAppToast from "../../../../commons/HallAppToast";
import { normalizeString } from "../../../../commons/util/HallAppUtil";

const companyAPI = new CompanyAPI();
const categoryAPI = new CategoryAPI();

/* ===================== CONSTANTS ===================== */

const ASSIGNMENT_MODES = [
  {
    key: "FIXED",
    label: "Profissional fixo",
    description: "Você define exatamente quem executa cada serviço.",
  },
  {
    key: "FLEXIBLE",
    label: "Profissionais flexíveis",
    description: "O sistema escolhe automaticamente entre os profissionais permitidos.",
  },
];

const DISCOUNT_TYPES = [
  { key: "PERCENT", label: "% de desconto" },
  { key: "FIXED", label: "R$ desconto" },
  { key: "FINAL_PRICE", label: "Preço final" },
];

/* ===================== BOTTOM SHEET SERVICES ===================== */

function BottomSheetServicePicker({ show, onClose, categories, selectedIds, onConfirm }) {
  const [q, setQ] = useState("");
  const [tmp, setTmp] = useState([]);

  useEffect(() => {
    if (show) {
      setQ("");
      setTmp(selectedIds || []);
    }
  }, [show, selectedIds]);

  const filtered = useMemo(() => {
    const nq = normalizeString(q);
    if (!nq) return categories;

    return (categories || [])
      .map((c) => ({
        ...c,
        services: (c.services || []).filter((s) =>
          normalizeString(`${s.name} ${c.name}`).includes(nq)
        ),
      }))
      .filter((c) => (c.services || []).length);
  }, [q, categories]);

  const toggle = (id) => {
    setTmp((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      fullscreen="md-down"
      centered
      contentClassName="hallapp-modal"
      dialogClassName="bottomsheet-modal"
    >
      <Modal.Header closeButton>
        <div className="w-100">
          <div style={{ fontWeight: 800 }}>Selecionar serviços</div>
          <AppInput
            label="Pesquisar"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            errors={{}}
            touched={{}}
          />
        </div>
      </Modal.Header>

      <Modal.Body>
        {(filtered || []).map((cat) => (
          <div key={cat.id} className="mb-3">
            <div style={{ fontSize: 12, fontWeight: 800 }}>{cat.name}</div>

            {(cat.services || []).map((s) => {
              const checked = tmp.includes(s.id);

              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => toggle(s.id)}
                  className={`service-pick-row ${checked ? "active" : ""}`}
                >
                  <div className={`service-pick-check ${checked ? "checked" : ""}`}>
                    {checked ? "✓" : ""}
                  </div>
                  <div className="service-pick-name">
                    <strong>{s.name}</strong>
                  </div>
                </button>
              );
            })}
          </div>
        ))}
      </Modal.Body>

      <div className="service-pick-footer">
        <AppLinkButton
          name="Confirmar"
          fullWidth
          onClick={() => {
            onConfirm(tmp);
            onClose();
          }}
        />
      </div>
    </Modal>
  );
}

/* ===================== FLEXIBLE EMPLOYEE CHECKLIST ===================== */

function EmployeeChecklist({ employees, selectedIds, onChange }) {
  const selected = Array.isArray(selectedIds) ? selectedIds : [];

  const toggle = (employeeId) => {
    const id = Number(employeeId);
    if (!id) return;

    const next = selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
    onChange(next);
  };

  if (!employees?.length) {
    return <div className="employee-empty">Nenhum profissional disponível para este serviço.</div>;
  }

  return (
    <div className="employee-checklist">
      {employees.map((e) => {
        const id = Number(e.employeeId);
        const checked = selected.includes(id);

        return (
          <button
            key={id}
            type="button"
            className={`employee-row ${checked ? "active" : ""}`}
            onClick={() => toggle(id)}
          >
            <div className={`employee-check ${checked ? "checked" : ""}`}>{checked ? "✓" : ""}</div>

            <div className="employee-info">
              <div className="employee-name">{e.name}</div>
              <div className="employee-sub">Toque para {checked ? "remover" : "selecionar"}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ===================== STEP 2 ===================== */

function PromotionStep2({ nextStep, toBack, initialData }) {
  const { Formik } = formik;

  const [categories, setCategories] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const [assignmentMode, setAssignmentMode] = useState("FIXED");
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [serviceAssignments, setServiceAssignments] = useState({});
  const [moneyValue, setMoneyValue] = useState(0);

  /* ===================== LOAD CATEGORIES ===================== */

  useEffect(() => {
    (async () => {
      try {
        const resp = await categoryAPI.findAllByCompanyScheduler(companyAPI.getCompanyManagment().id);
        setCategories(resp.data || []);
      } catch {
        HallAppToast.genericError();
      }
    })();
  }, []);

  /* ===================== LOOKUPS ===================== */

  const serviceLookup = useMemo(() => {
    const map = new Map();
    (categories || []).forEach((c) => (c.services || []).forEach((s) => map.set(s.id, s)));
    return map;
  }, [categories]);

  const selectedServices = useMemo(() => {
    return (selectedServiceIds || []).map((id) => serviceLookup.get(id)).filter(Boolean);
  }, [selectedServiceIds, serviceLookup]);

  /* ===================== HYDRATE (CREATE + EDIT + BACK/FORTH) ===================== */

  const hydrateFromInitialData = () => {
    const mode = initialData?.assignmentMode || "FIXED";
    setAssignmentMode(mode);

    const es = Array.isArray(initialData?.employeeServices) ? initialData.employeeServices : [];

    const idsFromServiceIds = Array.isArray(initialData?.serviceIds)
      ? initialData.serviceIds.map((n) => Number(n)).filter(Boolean)
      : [];

    const idsFromES = es.map((x) => Number(x?.serviceId)).filter(Boolean);

    const ids = idsFromServiceIds.length ? idsFromServiceIds : idsFromES;

    setSelectedServiceIds(ids);

    const nextAssignments = {};
    es.forEach((x) => {
      const sid = Number(x?.serviceId);
      if (!sid) return;

      const list =
        Array.isArray(x?.employeeIds) && x.employeeIds.length
          ? x.employeeIds.map((n) => Number(n)).filter(Boolean)
          : x?.employeeId
          ? [Number(x.employeeId)]
          : [];

      nextAssignments[sid] = { employeeIds: list };
    });

    // garante keys pra todos os serviços selecionados
    ids.forEach((sid) => {
      if (!nextAssignments[sid]) nextAssignments[sid] = { employeeIds: [] };
    });

    setServiceAssignments(nextAssignments);

    const dt = initialData?.discountType || "PERCENT";
    if (dt !== "PERCENT") {
      const v = Number(initialData?.discountValue);
      setMoneyValue(Number.isFinite(v) ? v : 0);
    } else {
      setMoneyValue(0);
    }
  };

  useEffect(() => {
    if (!categories?.length) return; // espera categories
    hydrateFromInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    categories?.length,
    initialData?.assignmentMode,
    JSON.stringify(initialData?.serviceIds || []),
    JSON.stringify(initialData?.employeeServices || []),
    initialData?.discountType,
    String(initialData?.discountValue ?? ""),
  ]);

  /* ===================== ASSIGNMENTS ===================== */

  const updateAssignment = (serviceId, employeeIds) => {
    setServiceAssignments((prev) => ({
      ...(prev || {}),
      [serviceId]: { employeeIds },
    }));
  };

  const validateAssignments = () => {
    if (!selectedServiceIds.length) {
      HallAppToast.error("Selecione ao menos um serviço.");
      return false;
    }

    // FIXED: exige 1 profissional por serviço
    if (assignmentMode === "FIXED") {
      for (const id of selectedServiceIds) {
        const ids = serviceAssignments[id]?.employeeIds || [];
        if (!ids.length) {
          HallAppToast.error("Selecione o profissional de todos os serviços.");
          return false;
        }
        if (ids.length !== 1) {
          HallAppToast.error("No modo fixo, selecione apenas 1 profissional por serviço.");
          return false;
        }
      }
      return true;
    }

    // FLEXIBLE: >=1 por serviço
    for (const id of selectedServiceIds) {
      const ids = serviceAssignments[id]?.employeeIds || [];
      if (!ids.length) {
        const svc = serviceLookup.get(id);
        HallAppToast.error(`Selecione ao menos um profissional em: ${svc?.name || "serviço"}.`);
        return false;
      }
    }

    // FLEXIBLE: interseção quando combo (2+ serviços)
    if (selectedServiceIds.length >= 2) {
      const lists = selectedServiceIds.map((id) => serviceAssignments[id]?.employeeIds || []);
      const intersection = lists.reduce((acc, list) => acc.filter((x) => list.includes(x)), lists[0] || []);
      if (!intersection.length) {
        HallAppToast.error("Os serviços não possuem um profissional comum para execução conjunta.");
        return false;
      }
    }

    return true;
  };

  /* ===================== NEXT ===================== */

  const onNext = (values) => {
    if (!validateAssignments()) return;

    nextStep({
      assignmentMode,
      serviceIds: selectedServiceIds,
      selectedServices,
      employeeServices: selectedServiceIds.map((id) => ({
        serviceId: id,
        employeeIds: serviceAssignments[id]?.employeeIds || [],
      })),
      discountType: values.discountType,
      discountValue: values.discountType === "PERCENT" ? values.discountValue : Number(moneyValue || 0),
    });
  };

  /* ===================== FORMIK INIT ===================== */

  const formikInitialValues = useMemo(() => {
    const dt = initialData?.discountType || "PERCENT";
    return {
      discountType: dt,
      discountValue: dt === "PERCENT" ? (initialData?.discountValue ?? "") : "",
    };
  }, [initialData?.discountType, initialData?.discountValue]);

  return (
    <>
      {/* ===================== SERVICES ===================== */}
      <div className="mb-3">
        <div style={{ fontWeight: 800 }}>Serviços da promoção *</div>
        <div className="text-muted" style={{ fontSize: 12 }}>
          {selectedServices.length ? `${selectedServices.length} serviço(s) selecionado(s)` : "Nenhum serviço selecionado"}
        </div>

        <div className="mt-2">
          <AppButton name="Selecionar serviços" onClick={() => setPickerOpen(true)} />
        </div>

        {selectedServices.length > 0 && (
          <div className="selected-services-chips">
            {selectedServices.map((s) => (
              <div key={s.id} className="selected-service-chip">
                {s.name}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ===================== ASSIGNMENT MODE ===================== */}
      <Row className="g-2 mb-3">
        {ASSIGNMENT_MODES.map((m) => (
          <Col md={6} key={m.key}>
            <button
              type="button"
              onClick={() => setAssignmentMode(m.key)}
              className={`assign-mode-card w-100 ${assignmentMode === m.key ? "active" : ""}`}
            >
              <div className="assign-mode-title">{m.label}</div>
              <div className="assign-mode-desc">{m.description}</div>
              {m.key === "FLEXIBLE" && <div className="assign-mode-badge">Permite 1 ou mais profissionais</div>}
            </button>
          </Col>
        ))}
      </Row>

      {/* ===================== PROFESSIONALS ===================== */}
      {selectedServices.map((svc) => {
        const employees = svc.employees || [];
        const selected = serviceAssignments[svc.id]?.employeeIds || [];

        return (
          <div key={svc.id} className="service-box">
            <div className="service-box-header">
              <div className="service-box-title">{svc.name}</div>
              <div className="service-box-sub">
                {assignmentMode === "FLEXIBLE" ? "Selecione 1 ou mais profissionais permitidos" : "Selecione 1 profissional"}
              </div>
            </div>

            {assignmentMode === "FIXED" ? (
              <Form.Select
                value={selected?.[0] ?? ""}
                onChange={(e) => {
                  const id = e.target.value ? Number(e.target.value) : "";
                  updateAssignment(svc.id, id ? [id] : []);
                }}
                className="service-employee-select"
              >
                <option value="">Selecione…</option>
                {employees.map((e) => (
                  <option key={e.employeeId} value={e.employeeId}>
                    {e.name}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <EmployeeChecklist
                employees={employees}
                selectedIds={selected}
                onChange={(ids) => updateAssignment(svc.id, ids)}
              />
            )}
          </div>
        );
      })}

      {/* ===================== DISCOUNT ===================== */}
      <Formik
        enableReinitialize
        initialValues={formikInitialValues}
        validationSchema={yup.object({
          discountType: yup.string().required(),
          discountValue: yup.string().when("discountType", {
            is: "PERCENT",
            then: (s) => s.required("Informe o desconto"),
          }),
        })}
      >
        {({ values, handleChange, errors, touched }) => (
          <>
            <div className="promo-discount-type">
              <div className="promo-discount-title">
                Tipo de promoção <span style={{ color: "red" }}>*</span>
              </div>

              <div className="promo-discount-options">
                {DISCOUNT_TYPES.map((opt) => {
                  const active = values.discountType === opt.key;

                  return (
                    <button
                      key={opt.key}
                      type="button"
                      className={`promo-discount-card ${active ? "active" : ""}`}
                      onClick={() =>
                        handleChange({
                          target: { name: "discountType", value: opt.key },
                        })
                      }
                    >
                      <div className="promo-discount-label">{opt.label}</div>
                      <div className="promo-discount-hint">
                        {opt.key === "PERCENT" && "Ex: 10%"}
                        {opt.key === "FIXED" && "Ex: R$ 20,00"}
                        {opt.key === "FINAL_PRICE" && "Ex: R$ 99,90"}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {values.discountType === "PERCENT" ? (
              <AppInput
                label="Desconto (%)"
                name="discountValue"
                value={values.discountValue}
                onChange={handleChange}
                errors={errors}
                touched={touched}
              />
            ) : (
              <AppCurrencyInput
                label="Valor (R$)"
                value={moneyValue}
                onChange={(_, v) => setMoneyValue(v)}
                errors={errors}
                touched={touched}
              />
            )}

            <div className="mt-3">
              <AppLinkButton name="Próximo" fullWidth onClick={() => onNext(values)} />
              <div className="mt-2">
                <AppButton name="Voltar" width="100%" onClick={toBack} />
              </div>
            </div>
          </>
        )}
      </Formik>

      {/* ===================== BOTTOM SHEET ===================== */}
      <BottomSheetServicePicker
        show={pickerOpen}
        onClose={() => setPickerOpen(false)}
        categories={categories}
        selectedIds={selectedServiceIds}
        onConfirm={(ids) => {
          const nextIds = Array.isArray(ids) ? ids : [];
          setSelectedServiceIds(nextIds);

          setServiceAssignments((prev) => {
            const next = { ...(prev || {}) };

            // remove serviços que saíram
            Object.keys(next).forEach((sid) => {
              if (!nextIds.includes(Number(sid))) delete next[sid];
            });

            // cria default vazio pros novos
            nextIds.forEach((sid) => {
              if (!next[sid]) next[sid] = { employeeIds: [] };
            });

            return next;
          });
        }}
      />
    </>
  );
}

export default PromotionStep2;
